import { useParams, RouteComponentProps } from '@reach/router';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { uploadBlobs, describeUploadError } from '../../utils/blobUpload';
import 'react-datepicker/dist/react-datepicker.css';
import Button from '../../components/Button';
import Error from '../../components/Error';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/Input';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';
import Select from '../../components/Select';
import Table, { TableData, TableHeader, TableRow } from '../../components/Table';
import { Tab, TabsContainer } from '../../components/Tabs';
import TextArea from '../../components/TextArea';
import { domain } from '../../d-man';
import {
    CallOff,
    CallOffItem,
    useCreateCallOffDelivery,
    useGetCallOff,
    useSendCallOff,
} from '../../queries/Calloff';
import { Delivery, DeliveryDTO, DeliveryItem } from '../../queries/Delivery';
import { useGetPermissions } from '../../queries/Permissions';
import { ProjectAddress } from '../../queries/Projects';
import { RequisitionItem } from '../../queries/Requisitions';
import { datePickerInputClassName, handleWebDownload, validateEmail } from '../../utils/Utils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function CallOffDetail(props: RouteComponentProps) {
    // CONSTANTS - TODO: Move!
    const DECIMAL_PLACES = 3;

    const params = useParams();
    const getId = () => params.id;

    const [email, setEmail] = useState('');
    const [openSendCallOffModal, setOpenSendCallOffModal] = useState(false);
    const [openDownloadCallOffModal, setOpenDownloadCallOffModal] = useState(false);
    const [deliveryErrorMessage, setDeliveryErrorMessage] = useState('');
    const [sendCallOffErrorMessage, setSendCallOffErrorMessage] = useState('');
    const [openCreateDeliveryModal, setOpenCreateDeliveryModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [deliveryAddressId, setDeliveryAddressId] = useState('');

    const [deliveryDate, setDeliveryDate] = useState(new Date());
    const [deliveryNote, setDeliveryNote] = useState('');
    const [deliveryFiles, setDeliveryFiles] = useState([] as File[]);
    const [deliveryItems, setDeliveryItems] = useState([] as DeliveryItem[]);
    const [returnNote, setReturnNote] = useState(undefined as string | undefined);
    const [returnFiles, setReturnFiles] = useState([] as File[]);

    const callOffDeliveryCreate = useCreateCallOffDelivery(getId());

    const callOff = useGetCallOff(getId());
    const sendCallOff = useSendCallOff(getId());

    const getPermissions = useGetPermissions('ORDER', callOff.data?.orderId);

    useEffect(() => {
        if (callOff.data?.order) {
            const { lineItems } = callOff.data?.order;
            if (lineItems) {
                if (deliveryItems.length === 0) {
                    setDeliveryItems(
                        lineItems.map(
                            (i: RequisitionItem) =>
                                ({
                                    lineItemId: i.id,
                                    quantity: 0,
                                    returnedQuantity: 0,
                                } as DeliveryItem)
                        )
                    );
                }
            }
        }
    }, [callOff.data, deliveryItems]);

    useEffect(() => {
        if (
            callOff.data?.order?.requisition?.project?.addresses &&
            callOff.data?.order?.requisition?.project?.addresses?.length
        ) {
            setDeliveryAddressId(callOff.data?.order?.requisition?.project?.addresses[0].id || '');
        }
    }, [callOff.data]);

    const [tabs] = useState([
        { label: 'Materials', id: 'Materials' },
        { label: 'Deliveries', id: 'Deliveries' },
    ] as Tab[]);
    const [selectedTab, setSelectedTab] = useState('Materials');

    const toDeliveryDetail = (id?: string) => {
        if (props.navigate) {
            props.navigate(`/deliveries/${id}`);
        }
    };

    const toOrderDetail = (id?: string) => {
        if (props.navigate) {
            props.navigate(`/orders/${id}`);
        }
    };

    const handleCreateDeliveryModalOpen = () => {
        setOpenCreateDeliveryModal(true);
    };

    const handleCreateDeliveryModalClose = () => {
        setOpenCreateDeliveryModal(false);
    };

    const round = (value: number, decimals: number) => {
        return Number(`${Math.round(Number(`${value}e${decimals}`))}e-${decimals}`);
    };

    const updateUploadedFiles = (files: File[]) => {
        setDeliveryFiles(files);
    };

    const updateUploadedFile = (files: File[]) => {
        setReturnFiles(files);
    };

    const handleDownload = async (endpoint: string) => {
        const [, data, response] = await domain
            .get(endpoint, {
                requestConfig: {
                    responseType: 'blob', // important
                },
            })
            .execute();
        if (!data) return;
        handleWebDownload(data, response);
        callOff.reset();
    };

    const formatDeliveryAddresses = () => {
        if (callOff.data && callOff.data.order?.requisition?.project?.addresses) {
            const filteredAddresses = callOff.data.order?.requisition?.project?.addresses.map(
                (address: ProjectAddress, i) =>
                    ({
                        value: address.id,
                        label: `Address ${i + 1}`,
                    } as { value: string; label: string })
            );
            return filteredAddresses;
        }
        return [];
    };

    const getDeliveryAddressDetails = () => {
        if (
            callOff.data &&
            callOff.data.order?.requisition?.project?.addresses &&
            deliveryAddressId
        ) {
            const filteredAddress = callOff.data.order?.requisition?.project?.addresses.find(
                (i) => i.id === deliveryAddressId
            );
            const addressLines = [];
            if (filteredAddress?.address1) {
                addressLines.push(filteredAddress?.address1);
            }
            if (filteredAddress?.address2) {
                addressLines.push(filteredAddress?.address2);
            }
            if (filteredAddress?.address3) {
                addressLines.push(filteredAddress?.address3);
            }
            if (filteredAddress?.postCode) {
                addressLines.push(filteredAddress?.postCode);
            }
            return addressLines.join('\n') || '';
        }
        return '';
    };

    const postDownloadCallOff = async () => {
        setIsLoading(true);
        await handleDownload(
            `/calloffs/${callOff.data?.id}/download?deliveryAddressId=${deliveryAddressId}`
        );

        setIsLoading(false);
        setOpenDownloadCallOffModal(false);
    };

    const postSendCallOff = async () => {
        if (!email) {
            setSendCallOffErrorMessage('Please capture all the fields above');
        } else if (!validateEmail(email)) {
            setSendCallOffErrorMessage('Please provide a valid email address');
        } else {
            setIsLoading(true);
            await sendCallOff.execute({ email, deliveryAddressId });
            // I cant get this error to work!
            if (sendCallOff.error) {
                // Display create order error here
                setSendCallOffErrorMessage(sendCallOff.error);
            } else {
                setOpenSendCallOffModal(false);
            }
            callOff.reset();
            setIsLoading(false);
        }
    };

    const postCreateDelivery = async () => {
        if (
            !deliveryDate ||
            !deliveryNote ||
            (deliveryItems.every((i) => i.returnedQuantity <= 0) &&
                deliveryItems.every((i) => i.quantity <= 0))
        ) {
            setDeliveryErrorMessage('Please capture all the fields below');
        } else if (
            deliveryItems.some((i) => i.returnedQuantity) &&
            (returnFiles.length <= 0 || !returnNote)
        ) {
            setDeliveryErrorMessage('Please capture all the fields below');
        } else if (deliveryFiles.length <= 0) {
            setDeliveryErrorMessage('You must upload at least 1 file');
        } else if (callOff.data?.order) {
            try {
                const blobs = await uploadBlobs(deliveryFiles);
                const returnBlobs = await uploadBlobs(returnFiles);

                const data: DeliveryDTO = {
                    deliveredAt: deliveryDate,
                    note: deliveryNote,
                    items: deliveryItems.filter((i) => i.quantity > 0 || i.returnedQuantity > 0),
                    blobs,
                    returnNote,
                    returnBlobs,
                };

                const [error] = await callOffDeliveryCreate.execute(data);
                if (error) {
                    setDeliveryErrorMessage(error);
                    return;
                }

                handleCreateDeliveryModalClose();
                callOff.reset();
                callOff.execute(getId());
            } catch (error) {
                // Nothing caught these before, so a failed upload silently did
                // nothing at all.
                setDeliveryErrorMessage(describeUploadError(error));
            }
        }
    };

    useEffect(() => {
        if (callOffDeliveryCreate.error) {
            setDeliveryErrorMessage(callOffDeliveryCreate.error);
        }
    }, [callOffDeliveryCreate.error]);

    const getTotalDelivered = (reqItemParam: RequisitionItem, callOffParam?: CallOff) => {
        if (
            callOffParam &&
            callOffParam.callOffDeliveries &&
            callOffParam.callOffDeliveries.length > 0
        ) {
            const { quantity } = callOffParam.callOffDeliveries
                .map((i) =>
                    i.items
                        .filter((j) => j.lineItemId === reqItemParam.id)
                        .reduce((a, b) => ({ lineItemId: '', quantity: a.quantity + b.quantity }), {
                            lineItemId: '',
                            quantity: 0,
                        })
                )
                .reduce((a, b) => ({ lineItemId: '', quantity: a.quantity + b.quantity }), {
                    lineItemId: '',
                    quantity: 0,
                });
            return round(quantity, DECIMAL_PLACES);
        }
        return 0;
    };

    const renderCallOff = () => {
        if (!callOff || callOff.loading || !callOff.data?.order) {
            return <Loader message="Fetching Call-Off from server" />;
        }
        if (callOff.error) {
            return (
                <Error message={`Failed to fetch Call-Off from the server (${callOff.error})`} />
            );
        }
        if (callOff.data && callOff.data?.order) {
            const { description, createdAt, orderId, number } = callOff.data;
            return (
                <div>
                    <div className="bg-surface border border-border rounded-xl h-full m-4">
                        <div className="flex items-center justify-between gap-4 p-4 border-b border-border">
                            <div className="text-lg font-semibold text-foreground">
                                Call-Off Information
                            </div>
                            <Button onClick={() => toOrderDetail(orderId)}>Go To Order</Button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-label w-1/2">
                                    Call-Off No.
                                </div>
                                <div className="w-1/2">
                                    <Input
                                        value={`${number?.toString().padStart(6, '0')}`}
                                        type="text"
                                        disabled
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-label w-1/2">
                                    Description
                                </div>
                                <div className="w-1/2">
                                    <Input value={description} type="text" disabled />
                                </div>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-label w-1/2">Created</div>
                                <div className="w-1/2">
                                    <Input
                                        value={
                                            createdAt
                                                ? format(new Date(createdAt), 'yyyy-MM-dd')
                                                : ''
                                        }
                                        type="text"
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="border-t border-border mt-4" />
                        <div className="flex flex-col space-y-4 p-4">
                            {callOff.data &&
                                getPermissions.data?.items.find(
                                    (p) => p.code === 'Order_Download_CallOff'
                                )?.hasAccess && (
                                    <div>
                                        <Button onClick={() => setOpenDownloadCallOffModal(true)}>
                                            Download Call-Off
                                        </Button>
                                    </div>
                                )}
                            {callOff.data &&
                                callOff.data &&
                                getPermissions.data?.items.find(
                                    (p) => p.code === 'Order_Send_CallOff'
                                )?.hasAccess && (
                                    <div>
                                        <Button onClick={() => setOpenSendCallOffModal(true)}>
                                            Send Call-Off
                                        </Button>
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <>
            {!getPermissions.data && <Loader message="Loading call-off..." />}
            {getPermissions.data && callOff.data && (
                <div className="flex">
                    <Modal
                        title="Download Call-Off"
                        onClose={() => setOpenDownloadCallOffModal(false)}
                        isOpen={openDownloadCallOffModal}
                    >
                        <div className="flex justify-between">
                            <div className="w-full space-y-5">
                                <Select
                                    label="Delivery Address"
                                    placeholder="Address 1"
                                    options={formatDeliveryAddresses()}
                                    selected={deliveryAddressId}
                                    onChange={(event) => setDeliveryAddressId(event.target.value)}
                                />
                                <TextArea label="" value={getDeliveryAddressDetails()} disabled />
                            </div>
                        </div>
                        {sendCallOffErrorMessage && (
                            <div className="flex mb-5 mt-5 text-sm text-danger">
                                {sendCallOffErrorMessage}
                            </div>
                        )}
                        <div className="flex flex-row-reverse">
                            <div>
                                <Button onClick={postDownloadCallOff} disabled={isLoading}>
                                    {!isLoading ? 'Submit' : <div>Loading</div>}
                                </Button>
                            </div>
                        </div>
                    </Modal>
                    <Modal
                        title="Send Call-Off"
                        subtitle="Email Call-Off to email address"
                        onClose={() => setOpenSendCallOffModal(false)}
                        isOpen={openSendCallOffModal}
                    >
                        <div className="flex justify-between">
                            <div className="w-full space-y-5">
                                <Input
                                    label="Email Address"
                                    value={email}
                                    type="email"
                                    onChange={(event) => setEmail(event.target.value)}
                                />
                                <Select
                                    label="Delivery Address"
                                    placeholder="Address 1"
                                    options={formatDeliveryAddresses()}
                                    selected={deliveryAddressId}
                                    onChange={(event) => setDeliveryAddressId(event.target.value)}
                                />
                                <TextArea label="" value={getDeliveryAddressDetails()} disabled />
                            </div>
                        </div>
                        {sendCallOffErrorMessage && (
                            <div className="flex mb-5 mt-5 text-sm text-danger">
                                {sendCallOffErrorMessage}
                            </div>
                        )}
                        <div className="flex flex-row-reverse">
                            <div>
                                <Button onClick={postSendCallOff} disabled={isLoading}>
                                    {!isLoading ? 'Submit' : <div>Loading</div>}
                                </Button>
                            </div>
                        </div>
                    </Modal>
                    {callOff.data?.order && (
                        <Modal
                            title="New Delivery"
                            // subtitle={`${order.data.number.toString().padStart(6, '0')}`}
                            onClose={handleCreateDeliveryModalClose}
                            isOpen={openCreateDeliveryModal}
                            onAction={postCreateDelivery}
                            loading={callOffDeliveryCreate.loading}
                        >
                            <div>
                                {deliveryErrorMessage && (
                                    <div className="flex mb-5 text-sm text-danger font-semibold">
                                        {deliveryErrorMessage}
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <div>
                                        <div className="w-full h-full mb-5">
                                            <div className="text-sm font-medium text-label pb-1.5">
                                                Material Information
                                            </div>
                                            <div className="bg-surface border border-border rounded-xl overflow-x-auto overflow-y-hidden">
                                                <table className="w-full border-collapse">
                                                    <thead>
                                                        <TableRow>
                                                            <TableHeader>Description</TableHeader>
                                                            <TableHeader>Cost Code</TableHeader>
                                                            <TableHeader>Creditor</TableHeader>
                                                            <TableHeader>
                                                                Quantity Remaining
                                                            </TableHeader>
                                                            <TableHeader>Delivered</TableHeader>
                                                            <TableHeader>Returned</TableHeader>
                                                        </TableRow>
                                                    </thead>
                                                    <tbody>
                                                        {callOff.data &&
                                                            callOff.data?.order &&
                                                            callOff.data?.order.lineItems &&
                                                            deliveryItems.length > 0 &&
                                                            callOff.data.items.map(
                                                                (callOffItem: CallOffItem) => {
                                                                    if (
                                                                        callOff.data?.order &&
                                                                        callOff.data?.order
                                                                            .lineItems
                                                                    ) {
                                                                        const reqItem = callOff.data?.order.lineItems.filter(
                                                                            (i) =>
                                                                                i.id ===
                                                                                callOffItem.lineItemId
                                                                        )[0];
                                                                        return (
                                                                            <TableRow hover>
                                                                                <TableData
                                                                                    id={reqItem.id}
                                                                                >
                                                                                    {
                                                                                        reqItem.description
                                                                                    }
                                                                                </TableData>
                                                                                <TableData>
                                                                                    {reqItem.costCode &&
                                                                                        reqItem
                                                                                            .costCode
                                                                                            .name}
                                                                                </TableData>
                                                                                <TableData>
                                                                                    {reqItem
                                                                                        .creditor
                                                                                        ?.name ||
                                                                                        ''}
                                                                                </TableData>
                                                                                <TableData>
                                                                                    {callOffItem.quantity &&
                                                                                        round(
                                                                                            callOffItem.quantity -
                                                                                                getTotalDelivered(
                                                                                                    reqItem,
                                                                                                    callOff.data
                                                                                                ),
                                                                                            DECIMAL_PLACES
                                                                                        )}
                                                                                </TableData>
                                                                                <TableData>
                                                                                    <Input
                                                                                        value={deliveryItems
                                                                                            .filter(
                                                                                                (
                                                                                                    i
                                                                                                ) =>
                                                                                                    i.lineItemId ===
                                                                                                    reqItem.id
                                                                                            )[0]
                                                                                            .quantity?.toString()}
                                                                                        onChange={(
                                                                                            event
                                                                                        ) => {
                                                                                            const oldDeliveryItem = deliveryItems.find(
                                                                                                (
                                                                                                    i
                                                                                                ) =>
                                                                                                    i.lineItemId ===
                                                                                                    reqItem.id
                                                                                            );
                                                                                            const newDeliveryItems = deliveryItems.filter(
                                                                                                (
                                                                                                    i
                                                                                                ) =>
                                                                                                    i.lineItemId !==
                                                                                                    reqItem.id
                                                                                            );
                                                                                            newDeliveryItems.push(
                                                                                                {
                                                                                                    lineItemId:
                                                                                                        reqItem.id,
                                                                                                    quantity: parseFloat(
                                                                                                        event
                                                                                                            .target
                                                                                                            .value ||
                                                                                                            '0'
                                                                                                    ),
                                                                                                    returnedQuantity:
                                                                                                        oldDeliveryItem?.returnedQuantity,
                                                                                                } as DeliveryItem
                                                                                            );
                                                                                            setDeliveryItems(
                                                                                                newDeliveryItems
                                                                                            );
                                                                                        }}
                                                                                        type="number"
                                                                                    />
                                                                                </TableData>
                                                                                <TableData>
                                                                                    <Input
                                                                                        value={deliveryItems
                                                                                            .filter(
                                                                                                (
                                                                                                    i
                                                                                                ) =>
                                                                                                    i.lineItemId ===
                                                                                                    reqItem.id
                                                                                            )[0]
                                                                                            .returnedQuantity?.toString()}
                                                                                        onChange={(
                                                                                            event
                                                                                        ) => {
                                                                                            const oldDeliveryItem = deliveryItems.find(
                                                                                                (
                                                                                                    i
                                                                                                ) =>
                                                                                                    i.lineItemId ===
                                                                                                    reqItem.id
                                                                                            );
                                                                                            const newDeliveryItems = deliveryItems.filter(
                                                                                                (
                                                                                                    i
                                                                                                ) =>
                                                                                                    i.lineItemId !==
                                                                                                    reqItem.id
                                                                                            );
                                                                                            newDeliveryItems.push(
                                                                                                {
                                                                                                    lineItemId:
                                                                                                        reqItem.id,
                                                                                                    returnedQuantity: parseFloat(
                                                                                                        event
                                                                                                            .target
                                                                                                            .value ||
                                                                                                            '0'
                                                                                                    ),
                                                                                                    quantity:
                                                                                                        oldDeliveryItem?.quantity,
                                                                                                } as DeliveryItem
                                                                                            );
                                                                                            setDeliveryItems(
                                                                                                newDeliveryItems
                                                                                            );
                                                                                        }}
                                                                                        type="number"
                                                                                    />
                                                                                </TableData>
                                                                            </TableRow>
                                                                        );
                                                                    }
                                                                    return <></>;
                                                                }
                                                            )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ml-5">
                                        <div className="mb-5">
                                            <div className="text-sm font-medium text-label pb-1.5">
                                                Delivery Date
                                            </div>
                                            <div>
                                                <DatePicker
                                                    dateFormat="yyyy-MM-dd"
                                                    selected={deliveryDate}
                                                    onChange={(date: Date) => setDeliveryDate(date)}
                                                    className={datePickerInputClassName}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-5">
                                            <Input
                                                label="Delivery Note"
                                                placeholder="Add Note Here"
                                                value={deliveryNote}
                                                type="text"
                                                onChange={(event) =>
                                                    setDeliveryNote(event.target.value)
                                                }
                                            />
                                        </div>
                                        <div className="mb-5">
                                            <div className="text-sm font-medium text-label pb-1.5">
                                                Upload Documents
                                            </div>
                                            <FileUpload
                                                updateFilesCb={updateUploadedFiles}
                                                // accept=".jpg,.png,.jpeg"
                                                multiple
                                            />
                                        </div>
                                        <div className="mb-5">
                                            <div className="text-sm font-medium text-label pb-1.5">
                                                Return (Optional)
                                            </div>
                                            <div className="border-t border-border-subtle mb-3" />
                                            <Input
                                                label="Return Note"
                                                placeholder="Add Note Here"
                                                value={returnNote}
                                                type="text"
                                                onChange={(event) =>
                                                    setReturnNote(event.target.value)
                                                }
                                            />
                                            <FileUpload
                                                multiple={false}
                                                updateFilesCb={updateUploadedFile}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    )}
                    <div>{renderCallOff()}</div>
                    <div>
                        <div className="bg-surface border border-border w-full rounded-xl h-full m-4">
                            <div className="p-5">
                                <TabsContainer
                                    selectedTab={selectedTab}
                                    options={tabs}
                                    onClick={setSelectedTab}
                                >
                                    {selectedTab === 'Materials' && (
                                        <div className="w-full h-full m-4">
                                            <div className="bg-surface border border-border rounded-xl overflow-x-auto overflow-y-hidden">
                                                <table className="w-full border-collapse">
                                                    <thead>
                                                        <TableRow>
                                                            <TableHeader>Description</TableHeader>
                                                            <TableHeader>
                                                                Call-Off Quantity Remaining
                                                            </TableHeader>
                                                            <TableHeader>Cost Code</TableHeader>
                                                            <TableHeader>Creditor</TableHeader>
                                                        </TableRow>
                                                    </thead>
                                                    <tbody>
                                                        {callOff.data &&
                                                            callOff.data?.order &&
                                                            callOff.data.items &&
                                                            callOff.data.items.map(
                                                                (callOffItem: CallOffItem) => {
                                                                    if (
                                                                        callOff.data?.order &&
                                                                        callOff.data?.order
                                                                            .lineItems
                                                                    ) {
                                                                        const reqItem = callOff.data?.order.lineItems.filter(
                                                                            (i) =>
                                                                                i.id ===
                                                                                callOffItem.lineItemId
                                                                        )[0];
                                                                        return (
                                                                            <TableRow hover>
                                                                                <TableData
                                                                                    id={reqItem.id}
                                                                                >
                                                                                    {
                                                                                        reqItem.description
                                                                                    }
                                                                                </TableData>
                                                                                <TableData>
                                                                                    {round(
                                                                                        callOffItem.quantity -
                                                                                            getTotalDelivered(
                                                                                                reqItem,
                                                                                                callOff.data
                                                                                            ),
                                                                                        DECIMAL_PLACES
                                                                                    )}
                                                                                </TableData>
                                                                                <TableData>
                                                                                    {
                                                                                        reqItem
                                                                                            .costCode
                                                                                            ?.name
                                                                                    }
                                                                                </TableData>
                                                                                <TableData>
                                                                                    {reqItem
                                                                                        .creditor
                                                                                        ?.name ||
                                                                                        ''}
                                                                                </TableData>
                                                                            </TableRow>
                                                                        );
                                                                    }
                                                                    return <></>;
                                                                }
                                                            )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                    {selectedTab === 'Deliveries' && (
                                        <div>
                                            <div className="flex flex-row-reverse m-5">
                                                <Button
                                                    onClick={() => handleCreateDeliveryModalOpen()}
                                                >
                                                    Add Delivery
                                                </Button>
                                            </div>
                                            <div className="text-lg font-semibold text-foreground mb-3">
                                                Delivery History
                                            </div>
                                            {callOff.data && callOff.data.callOffDeliveries && (
                                                <Table<Delivery>
                                                    data={callOff.data.callOffDeliveries}
                                                    onClick={(i) => toDeliveryDetail(i.id)}
                                                    columns={[
                                                        {
                                                            key: 'note',
                                                            title: 'Delivery Note',
                                                            render: (delivery) =>
                                                                `${delivery.note}`,
                                                        },
                                                        {
                                                            key: 'deliveredAt',
                                                            title: 'Delivered At',
                                                            render: (delivery) =>
                                                                format(
                                                                    new Date(
                                                                        delivery.deliveredAt || ''
                                                                    ),
                                                                    'yyyy-MM-dd'
                                                                ),
                                                        },
                                                    ]}
                                                />
                                            )}
                                        </div>
                                    )}
                                </TabsContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
